import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
    FaCheck, FaRegCopy, FaCreditCard, FaTimes, FaArrowLeft
} from 'react-icons/fa';
import { QRCodeCanvas as QRCode } from 'qrcode.react';
import axios from 'axios';

// Configuração da API
const API_BASE_URL = 'http://localhost:3001'; // Altere para a URL da sua API

const CheckoutPage = () => {
    const location = useLocation();
    const navigate = useNavigate();

    const { selectedPlan } = location.state || { selectedPlan: 'basic' };

    const [nome, setNome] = useState("");
    const [cpfCnpj, setCpfCnpj] = useState("");
    const [email, setEmail] = useState("");
    const [telefone, setTelefone] = useState("");
    const [loading, setLoading] = useState(false);
    const [step, setStep] = useState('form');
    const [paymentData, setPaymentData] = useState(null);
    const [paymentMethod, setPaymentMethod] = useState('');
    const [cardData, setCardData] = useState({
        number: '',
        expiryMonth: '',
        expiryYear: '',
        ccv: '',
        name: ''
    });

    const planValues = {
        basic: 175.00,
        premium: 350.00
    };

    const handleCardChange = (e) => {
        const { name, value } = e.target;

        if (name === 'number') {
            const numericValue = value.replace(/\D/g, '').slice(0, 16);
            const formattedValue = numericValue.replace(/(\d{4})(?=\d)/g, '$1 ');
            setCardData(prev => ({ ...prev, [name]: formattedValue }));
        } else {
            setCardData(prev => ({ ...prev, [name]: value }));
        }
    };

    const validateForm = () => {
        if (!nome || !email || !telefone) {
            alert('Por favor, preencha todos os campos!');
            return false;
        }

        if (!email.includes('@') || !email.includes('.')) {
            alert('Por favor, insira um e-mail válido!');
            return false;
        }

        if (!(cpfCnpj.length === 11 || cpfCnpj.length === 14)) {
            alert('Por favor, insira um CPF (11 dígitos) ou CNPJ (14 dígitos) válido!');
            return false;
        }

        if (telefone.replace(/\D/g, '').length < 10) {
            alert('Por favor, insira um telefone válido com DDD!');
            return false;
        }

        return true;
    };

    const validateCard = () => {
        const { number, expiryMonth, expiryYear, ccv, name } = cardData;

        if (!number || number.replace(/\s/g, '').length !== 16) {
            alert('Número do cartão inválido!');
            return false;
        }

        if (!expiryMonth || !expiryYear || expiryMonth < 1 || expiryMonth > 12) {
            alert('Data de validade inválida!');
            return false;
        }

        if (!ccv || ccv.length < 3) {
            alert('Código de segurança inválido!');
            return false;
        }

        if (!name) {
            alert('Por favor, informe o nome impresso no cartão!');
            return false;
        }

        return true;
    };

    const handlePayment = async () => {
        if (!validateForm()) return;

        setLoading(true);

        try {
            const payload = {
                nome,
                cpfCnpj,
                email,
                telefone,
                paymentMethod,
                cartao: paymentMethod === 'credit_card' ? cardData : undefined
            };

            const endpoint = selectedPlan === 'basic' ? '/api/v1/pagamento/basic' : '/api/v1/pagamento/premium';
            const url = `${API_BASE_URL}${endpoint}`;

            const response = await axios.post(url, payload, {
                headers: {
                    'Content-Type': 'application/json'
                }
            });

            setPaymentData(response.data);

            if (paymentMethod === 'pix') {
                setStep('pix');
            } else if (paymentMethod === 'credit_card') {
                setStep('success');
            }
        } catch (error) {
            console.error('Erro no pagamento:', error);
            alert(`Erro ao processar pagamento: ${error.response?.data?.error || error.message}`);
        } finally {
            setLoading(false);
        }
    };

    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text);
        alert('Copiado para a área de transferência!');
    };

    const formatCurrency = (value) => {
        return value.toLocaleString('pt-BR', {
            style: 'currency',
            currency: 'BRL'
        });
    };

    const renderStep = () => {
        switch (step) {
            case 'form':
                return (
                    <div className="space-y-6">
                        <h2 className="text-2xl font-bold mb-6">Informações Pessoais</h2>

                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Nome Completo / Razão Social</label>
                                <input
                                    type="text"
                                    name="nome"
                                    placeholder="Nome Completo / Razão Social"
                                    value={nome}
                                    onChange={e => setNome(e.target.value)}
                                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-green-500"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">CPF/CNPJ</label>
                                <input
                                    type="text"
                                    name="cpf/cnpj"
                                    placeholder="CPF / CNPJ"
                                    value={cpfCnpj}
                                    onChange={e => setCpfCnpj(e.target.value)}
                                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-green-500"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">E-mail</label>
                                <input
                                    type="email"
                                    name="email"
                                    placeholder="Seu melhor e-mail"
                                    value={email}
                                    onChange={e => setEmail(e.target.value)}
                                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-green-500"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Telefone</label>
                                <input
                                    type="tel"
                                    name="telefone"
                                    placeholder="Seu telefone com DDD"
                                    value={telefone}
                                    onChange={e => setTelefone(e.target.value)}
                                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-green-500"
                                />
                            </div>
                        </div>

                        <div className="pt-4 border-t border-gray-200">
                            <h4 className="font-semibold mb-2">Resumo do Pedido</h4>
                            <div className="flex justify-between mb-1">
                                <span>Produto:</span>
                                <span>{selectedPlan === 'basic' ? 'Teste Básico' : 'Teste Premium'}</span>
                            </div>
                            <div className="flex justify-between font-bold text-lg">
                                <span>Total:</span>
                                <span>{formatCurrency(planValues[selectedPlan])}</span>
                            </div>
                        </div>

                        <button
                            onClick={() => setStep('method')}
                            disabled={!nome || !cpfCnpj || !email || !telefone}
                            className="w-full bg-green-600 hover:bg-green-700 text-white font-bold py-3 px-4 rounded-md shadow-sm disabled:opacity-50"
                        >
                            Continuar para pagamento
                        </button>

                        <button
                            onClick={() => navigate('/')}
                            className="w-full bg-gray-200 hover:bg-gray-300 py-2 rounded-lg font-medium transition"
                        >
                            Voltar para a página inicial
                        </button>
                    </div>
                );

            case 'method':
                return (
                    <div className="space-y-6">
                        <button
                            onClick={() => setStep('form')}
                            className="flex items-center text-gray-600 hover:text-gray-800 mb-4"
                        >
                            <FaArrowLeft className="mr-2" /> Voltar
                        </button>

                        <h2 className="text-2xl font-bold mb-6">Método de Pagamento</h2>

                        <div className="space-y-4">
                            <button
                                onClick={() => {
                                    setPaymentMethod('pix');
                                    handlePayment();
                                }}
                                className="w-full flex items-center justify-between p-4 border border-green-500 rounded-lg hover:bg-green-50 transition"
                            >
                                <div className="flex items-center">
                                    <FaRegCopy className="text-green-600 mr-3" size={24} />
                                    <span className="font-semibold">PIX</span>
                                </div>
                                <div className="text-sm text-gray-600">
                                    Pagamento instantâneo
                                </div>
                            </button>

                            <button
                                onClick={() => {
                                    setPaymentMethod('credit_card');
                                    setStep('credit_card');
                                }}
                                className="w-full flex items-center justify-between p-4 border border-blue-500 rounded-lg hover:bg-blue-50 transition"
                            >
                                <div className="flex items-center">
                                    <FaCreditCard className="text-blue-600 mr-3" size={24} />
                                    <span className="font-semibold">Cartão de Crédito</span>
                                </div>
                                <div className="text-sm text-gray-600">
                                    Parcele em até 12x
                                </div>
                            </button>
                        </div>
                    </div>
                );

            case 'pix':
                return (
                    <div className="text-center">
                        <button
                            onClick={() => setStep('method')}
                            className="flex items-center text-gray-600 hover:text-gray-800 mb-4"
                        >
                            <FaArrowLeft className="mr-2" /> Voltar
                        </button>

                        <h2 className="text-2xl font-bold mb-6">Pagamento via PIX</h2>

                        {paymentData ? (
                            <>
                                <div className="bg-gray-100 p-4 rounded-lg inline-block mb-4">
                                    <QRCode
                                        value={paymentData.payload}
                                        size={200}
                                        level="H"
                                        includeMargin
                                    />
                                </div>

                                <div className="mb-4">
                                    <p className="text-sm text-gray-600 mb-2">
                                        Escaneie o QR Code acima ou copie o código abaixo:
                                    </p>
                                    <div className="relative">
                                        <input
                                            type="text"
                                            value={paymentData.payload}
                                            readOnly
                                            className="w-full p-2 border border-gray-300 rounded text-sm font-mono"
                                        />
                                        <button
                                            onClick={() => copyToClipboard(paymentData.payload)}
                                            className="absolute right-2 top-2 text-gray-500 hover:text-gray-700"
                                        >
                                            <FaRegCopy />
                                        </button>
                                    </div>
                                </div>

                                <div className="bg-yellow-50 border-l-4 border-yellow-400 p-3 mb-4 text-left">
                                    <p className="text-sm text-yellow-700">
                                        <strong>Valor:</strong> {formatCurrency(paymentData.value)}<br />
                                        <strong>Expira em:</strong> {new Date(paymentData.expirationDate).toLocaleString()}
                                    </p>
                                </div>
                            </>
                        ) : (
                            <p>Carregando dados do PIX...</p>
                        )}
                    </div>
                );

            case 'credit_card':
                return (
                    <div className="space-y-6">
                        <button
                            onClick={() => setStep('method')}
                            className="flex items-center text-gray-600 hover:text-gray-800 mb-4"
                        >
                            <FaArrowLeft className="mr-2" /> Voltar
                        </button>

                        <h2 className="text-2xl font-bold mb-6">Pagamento via Cartão de Crédito</h2>

                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Número do Cartão</label>
                                <input
                                    type="text"
                                    name="number"
                                    value={cardData.number}
                                    onChange={handleCardChange}
                                    placeholder="0000 0000 0000 0000"
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-green-500 focus:border-green-500"
                                    maxLength={19}
                                />
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Validade</label>
                                    <div className="grid grid-cols-2 gap-2">
                                        <input
                                            type="text"
                                            name="expiryMonth"
                                            value={cardData.expiryMonth}
                                            onChange={handleCardChange}
                                            placeholder="MM"
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-green-500 focus:border-green-500"
                                            maxLength="2"
                                        />
                                        <input
                                            type="text"
                                            name="expiryYear"
                                            value={cardData.expiryYear}
                                            onChange={handleCardChange}
                                            placeholder="AA"
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-green-500 focus:border-green-500"
                                            maxLength="2"
                                        />
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Código de Segurança</label>
                                    <input
                                        type="text"
                                        name="ccv"
                                        value={cardData.ccv}
                                        onChange={handleCardChange}
                                        placeholder="000"
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-green-500 focus:border-green-500"
                                        maxLength="4"
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Nome no Cartão</label>
                                <input
                                    type="text"
                                    name="name"
                                    value={cardData.name}
                                    onChange={handleCardChange}
                                    placeholder="Como impresso no cartão"
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-green-500 focus:border-green-500"
                                />
                            </div>

                            <button
                                onClick={() => {
                                    if (validateCard()) {
                                        handlePayment();
                                    }
                                }}
                                disabled={loading}
                                className="w-full bg-green-600 hover:bg-green-700 text-white font-bold py-3 px-4 rounded-md shadow-sm disabled:opacity-50"
                            >
                                {loading ? 'Processando...' : 'Confirmar Pagamento'}
                            </button>
                        </div>
                    </div>
                );

            case 'success':
                return (
                    <div className="text-center">
                        <div className="bg-green-100 p-6 rounded-lg mb-4">
                            <div className="w-16 h-16 bg-green-500 text-white rounded-full flex items-center justify-center mx-auto mb-4">
                                <FaCheck size={24} />
                            </div>
                            <h3 className="text-xl font-bold text-green-800 mb-2">Pagamento Aprovado!</h3>
                            <p className="text-green-700">Seu pagamento foi processado com sucesso.</p>
                        </div>

                        {paymentData && (
                            <div className="mb-6 text-left">
                                <h4 className="font-semibold mb-2">Detalhes do Pedido</h4>
                                <div className="space-y-2 text-sm">
                                    <div className="flex justify-between">
                                        <span className="text-gray-600">Número do Pedido:</span>
                                        <span className="font-medium">{paymentData.paymentId}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-gray-600">Plano:</span>
                                        <span className="font-medium">{selectedPlan === 'basic' ? 'Básico' : 'Premium'}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-gray-600">Valor:</span>
                                        <span className="font-medium">{formatCurrency(paymentData.value)}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-gray-600">Método:</span>
                                        <span className="font-medium">{paymentMethod === 'pix' ? 'PIX' : 'Cartão de Crédito'}</span>
                                    </div>
                                </div>
                            </div>
                        )}

                        {paymentData?.invoiceUrl && (
                            <a
                                href={paymentData.invoiceUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-block w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-md mb-4"
                            >
                                Visualizar Comprovante
                            </a>
                        )}

                        <button
                            onClick={() => navigate('/')}
                            className="w-full bg-gray-200 hover:bg-gray-300 py-2 rounded-lg font-medium transition"
                        >
                            Voltar para a página inicial
                        </button>
                    </div>
                );

            default:
                return null;
        }
    };

    return (
        <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
            <div className="max-w-md mx-auto bg-white rounded-xl shadow-md overflow-hidden md:max-w-2xl">
                <div className="bg-gradient-to-r from-green-600 to-green-700 p-6 text-white">
                    <h1 className="text-2xl font-bold">Finalizar Compra</h1>
                    <p className="text-green-100">
                        {selectedPlan === 'basic' ? 'Teste Básico' : 'Teste Premium'} - {formatCurrency(planValues[selectedPlan])}
                    </p>
                </div>

                <div className="p-6">
                    {renderStep()}
                </div>
            </div>
        </div>
    );
};

export default CheckoutPage;